import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    
    // Log error to console for debugging
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    
    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = "/";
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle className="text-xl">Something went wrong</CardTitle>
              <CardDescription>
                An unexpected error occurred. We apologize for the inconvenience.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {this.state.error && (
                <div className="rounded-lg bg-muted p-4 text-sm">
                  <p className="font-medium text-foreground mb-1">Error Details:</p>
                  <p className="text-muted-foreground font-mono text-xs break-all">
                    {this.state.error.message}
                  </p>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex flex-col sm:flex-row gap-2">
              <Button 
                variant="outline" 
                className="w-full sm:w-auto" 
                onClick={this.handleGoHome}
              >
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
              <Button 
                variant="outline" 
                className="w-full sm:w-auto" 
                onClick={this.handleReset}
              >
                Try Again
              </Button>
              <Button 
                className="w-full sm:w-auto" 
                onClick={this.handleRefresh}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Page
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Smaller inline error boundary for widgets/sections
interface InlineErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
  title?: string;
}

export function InlineErrorFallback({ 
  error, 
  resetError,
  title = "Failed to load" 
}: InlineErrorFallbackProps) {
  return (
    <div className="flex flex-col items-center justify-center p-6 text-center rounded-lg border border-dashed border-destructive/30 bg-destructive/5">
      <AlertTriangle className="h-8 w-8 text-destructive/60 mb-2" />
      <p className="text-sm font-medium text-foreground">{title}</p>
      {error && (
        <p className="text-xs text-muted-foreground mt-1 max-w-xs truncate">
          {error.message}
        </p>
      )}
      {resetError && (
        <Button 
          variant="ghost" 
          size="sm" 
          className="mt-3"
          onClick={resetError}
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          Retry
        </Button>
      )}
    </div>
  );
}

// Widget-level error boundary with compact UI
interface WidgetErrorBoundaryProps {
  children: ReactNode;
  widgetName?: string;
}

interface WidgetErrorState {
  hasError: boolean;
  error: Error | null;
}

export class WidgetErrorBoundary extends Component<WidgetErrorBoundaryProps, WidgetErrorState> {
  public state: WidgetErrorState = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<WidgetErrorState> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`Widget error (${this.props.widgetName || 'unknown'}):`, error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <InlineErrorFallback 
          error={this.state.error || undefined}
          resetError={this.handleReset}
          title={this.props.widgetName ? `${this.props.widgetName} failed to load` : "Widget failed to load"}
        />
      );
    }

    return this.props.children;
  }
}
